import { Menu, Transition } from '@headlessui/react';
import {
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { safeStorage } from '../../utils/safeStorage';
import NotificationDropdown from './NotificationDropdown';

// Built-in avatar options (same as ProfilePage)
const BUILT_IN_AVATARS = [
  { id: 'avatar-1', emoji: '👤', bg: 'bg-gray-500' },
  { id: 'avatar-2', emoji: '🧑‍💼', bg: 'bg-blue-500' },
  { id: 'avatar-3', emoji: '👨‍💼', bg: 'bg-indigo-500' },
  { id: 'avatar-4', emoji: '👩‍💼', bg: 'bg-purple-500' },
  { id: 'avatar-5', emoji: '🧑‍🔧', bg: 'bg-orange-500' },
  { id: 'avatar-6', emoji: '👨‍🔧', bg: 'bg-amber-500' },
  { id: 'avatar-7', emoji: '🚗', bg: 'bg-red-500' },
  { id: 'avatar-8', emoji: '🏎️', bg: 'bg-rose-500' },
  { id: 'avatar-9', emoji: '🚙', bg: 'bg-green-500' },
  { id: 'avatar-10', emoji: '🏁', bg: 'bg-teal-500' },
  { id: 'avatar-11', emoji: '⭐', bg: 'bg-yellow-500' },
  { id: 'avatar-12', emoji: '💼', bg: 'bg-slate-500' },
];

const GRADIENT_AVATARS = [
  { id: 'grad-1', initials: true, bg: 'bg-gradient-to-br from-blue-500 to-purple-600' },
  { id: 'grad-2', initials: true, bg: 'bg-gradient-to-br from-green-400 to-blue-500' },
  { id: 'grad-3', initials: true, bg: 'bg-gradient-to-br from-pink-500 to-orange-400' },
  { id: 'grad-4', initials: true, bg: 'bg-gradient-to-br from-purple-500 to-pink-500' },
  { id: 'grad-5', initials: true, bg: 'bg-gradient-to-br from-yellow-400 to-red-500' },
  { id: 'grad-6', initials: true, bg: 'bg-gradient-to-br from-indigo-500 to-cyan-400' },
];

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const [avatarId, setAvatarId] = useState<string | null>(null);
  const [avatarImage, setAvatarImage] = useState<string | null>(null);

  // Load avatar from localStorage on mount and listen for changes
  useEffect(() => {
    const loadAvatar = () => {
      const savedAvatar = safeStorage.getItem('userAvatar');
      const savedImage = safeStorage.getItem('userAvatarImage');
      setAvatarId(savedAvatar);
      setAvatarImage(savedImage);
    };

    loadAvatar();

    // Listen for storage changes (when profile page updates avatar)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userAvatar' || e.key === 'userAvatarImage') {
        loadAvatar();
      }
    };

    // Also listen for custom event for same-tab updates
    const handleAvatarUpdate = () => loadAvatar();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('avatarUpdated', handleAvatarUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('avatarUpdated', handleAvatarUpdate);
    };
  }, []);

  // Get avatar display info
  const getAvatarDisplay = () => {
    if (avatarImage) {
      return { type: 'image', src: avatarImage };
    }
    if (avatarId) {
      const builtIn = BUILT_IN_AVATARS.find((a) => a.id === avatarId);
      if (builtIn) {
        return { type: 'emoji', emoji: builtIn.emoji, bg: builtIn.bg };
      }
      const gradient = GRADIENT_AVATARS.find((a) => a.id === avatarId);
      if (gradient) {
        return { type: 'gradient', bg: gradient.bg };
      }
    }
    return { type: 'initials' };
  };

  const avatarDisplay = getAvatarDisplay();

  const renderAvatar = (size: 'sm' | 'md' = 'sm') => {
    const sizeClasses = size === 'sm' ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base';

    if (avatarDisplay.type === 'image') {
      return (
        <img
          src={avatarDisplay.src}
          alt="Profile"
          className={`${sizeClasses} rounded-full object-cover`}
        />
      );
    }

    if (avatarDisplay.type === 'emoji') {
      return (
        <div
          className={`${sizeClasses} ${avatarDisplay.bg} rounded-full flex items-center justify-center`}
        >
          <span className={size === 'sm' ? 'text-base' : 'text-lg'}>{avatarDisplay.emoji}</span>
        </div>
      );
    }

    if (avatarDisplay.type === 'gradient') {
      return (
        <div
          className={`${sizeClasses} ${avatarDisplay.bg} rounded-full flex items-center justify-center`}
        >
          <span className="text-white font-medium">
            {user?.first_name?.[0]}
            {user?.last_name?.[0]}
          </span>
        </div>
      );
    }

    // Default initials
    return (
      <div
        className={`${sizeClasses} bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center`}
      >
        <span className="text-gray-700 dark:text-gray-200 font-medium">
          {user?.first_name?.[0]}
          {user?.last_name?.[0]}
        </span>
      </div>
    );
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-16">
        {/* Left side - could add breadcrumbs or page title here */}
        <div className="flex-1">{/* Mobile menu button could go here */}</div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <NotificationDropdown />

          {/* User menu */}
          <Menu as="div" className="relative">
            <div>
              <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <span className="sr-only">Open user menu</span>
                <div className="flex items-center space-x-3">
                  {renderAvatar('sm')}
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {user?.role}
                    </p>
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-300" />
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none z-10">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={`${
                          active ? 'bg-gray-100 dark:bg-gray-700' : ''
                        } flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                      >
                        <UserCircleIcon className="mr-3 h-4 w-4" />
                        Your Profile
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={logout}
                        className={`${
                          active ? 'bg-gray-100 dark:bg-gray-700' : ''
                        } flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default Header;
